/**
 * AddProductDialog Component
 *
 * Dialog for creating a new Product (industry vertical).
 * Supports two product types:
 * - Platform: TerraMatrix-controlled vertical SaaS solutions (admin only)
 * - Organization: Org-specific custom workspaces
 */

import React, { useState, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Globe, Building2 } from 'lucide-react';
import { groupApi, CreateProductInput, ProductType } from '@/services/api/groupApi';
import { FOREST_GREEN } from '@/styles/tokens';
import * as errorUtils from '@/utils/errors';

// Common product icons for selection
const ICON_OPTIONS = [
  { value: '🍺', label: 'Brewing' },
  { value: '🌾', label: 'Agriculture' },
  { value: '🌿', label: 'Environmental' },
  { value: '💧', label: 'Water' },
  { value: '⚡', label: 'Energy' },
  { value: '🏭', label: 'Manufacturing' },
  { value: '🏢', label: 'Commercial' },
  { value: '🏠', label: 'Residential' },
  { value: '🚜', label: 'Farm Equipment' },
  { value: '📊', label: 'Analytics' },
  { value: '🔧', label: 'Equipment' },
  { value: '📦', label: 'General' },
];

interface AddProductDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
  productType?: ProductType;
}

export const AddProductDialog: React.FC<AddProductDialogProps> = ({
  open,
  onOpenChange,
  onSuccess,
  productType = 'organization',
}) => {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [description, setDescription] = useState('');
  const [icon, setIcon] = useState('📦');
  const [customDomain, setCustomDomain] = useState('');
  const [category, setCategory] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isPlatform = productType === 'platform';

  // Auto-generate slug from name
  const handleNameChange = useCallback((value: string) => {
    setName(value);
    // Generate slug: lowercase, replace spaces with hyphens, remove special chars
    const generatedSlug = value
      .toLowerCase()
      .replace(/\s+/g, '-')
      .replace(/[^a-z0-9-]/g, '');
    setSlug(generatedSlug);
  }, []);

  // Reset form when dialog closes
  const handleOpenChange = useCallback((isOpen: boolean) => {
    if (!isOpen) {
      setName('');
      setSlug('');
      setDescription('');
      setIcon('📦');
      setCustomDomain('');
      setCategory('');
      setError(null);
    }
    onOpenChange(isOpen);
  }, [onOpenChange]);

  // Handle form submission
  const handleSubmit = useCallback(async () => {
    if (!name.trim()) {
      setError('Product name is required');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      if (isPlatform) {
        // Create platform product (admin only)
        await groupApi.createPlatformProduct({
          name: name.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || undefined,
          icon,
          customDomain: customDomain.trim() || undefined,
          platformMeta: category.trim() ? { category: category.trim() } : undefined,
        });
      } else {
        // Create organization product
        await groupApi.createOrgProduct({
          name: name.trim(),
          slug: slug.trim() || undefined,
          description: description.trim() || undefined,
          icon,
        });
      }
      handleOpenChange(false);
      onSuccess();
    } catch (err: unknown) {
      const errorMessage =
        errorUtils.getErrorMessageOrFallback(err, 'Failed to create product');
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }, [name, slug, description, icon, customDomain, category, isPlatform, handleOpenChange, onSuccess]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {isPlatform ? (
              <>
                <Globe className="h-5 w-5 text-blue-600" />
                Create Platform Product
              </>
            ) : (
              <>
                <Building2 className="h-5 w-5 text-green-600" />
                Create Organization Product
              </>
            )}
          </DialogTitle>
          <DialogDescription>
            {isPlatform
              ? 'Platform products are TerraMatrix-controlled vertical SaaS solutions that organizations can subscribe to.'
              : 'Organization products are custom workspaces that belong to your organization.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">Name *</Label>
            <Input
              id="name"
              placeholder={isPlatform ? "e.g., Brewing, Agriculture" : "e.g., My Custom Workspace"}
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              {isPlatform
                ? 'The display name for this industry vertical'
                : 'The display name for your workspace'}
            </p>
          </div>

          {/* Slug */}
          <div className="space-y-2">
            <Label htmlFor="slug">Slug</Label>
            <Input
              id="slug"
              placeholder="Auto-generated from name"
              value={slug}
              onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
            />
            <p className="text-xs text-muted-foreground">
              URL-safe identifier (auto-generated, but can be customized)
            </p>
          </div>

          {/* Description */}
          <div className="space-y-2">
            <Label htmlFor="description">Description</Label>
            <Input
              id="description"
              placeholder={isPlatform ? "e.g., Craft brewery monitoring and automation" : "e.g., Custom IoT monitoring"}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>

          {/* Platform-only fields */}
          {isPlatform && (
            <>
              {/* Custom Domain */}
              <div className="space-y-2">
                <Label htmlFor="customDomain">Custom Domain</Label>
                <Input
                  id="customDomain"
                  placeholder="e.g., brewing.terramatrix.com"
                  value={customDomain}
                  onChange={(e) => setCustomDomain(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Optional custom domain for this platform product
                </p>
              </div>

              {/* Category */}
              <div className="space-y-2">
                <Label htmlFor="category">Category</Label>
                <Input
                  id="category"
                  placeholder="e.g., agriculture, manufacturing"
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Category for marketplace organization
                </p>
              </div>
            </>
          )}

          {/* Icon */}
          <div className="space-y-2">
            <Label>Icon</Label>
            <div className="flex flex-wrap gap-2">
              {ICON_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`w-10 h-10 text-xl rounded-lg border-2 transition-colors ${
                    icon === opt.value
                      ? 'border-primary bg-primary/10'
                      : 'border-border hover:border-muted-foreground'
                  }`}
                  onClick={() => setIcon(opt.value)}
                  title={opt.label}
                >
                  {opt.value}
                </button>
              ))}
            </div>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 rounded-lg bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-400 text-sm">
              {error}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isSubmitting}
            className={isPlatform ? "bg-blue-600 hover:bg-blue-700 text-white" : "text-white"}
            style={!isPlatform ? { backgroundColor: FOREST_GREEN.DEFAULT } : undefined}
            onMouseEnter={!isPlatform ? (e) => (e.currentTarget.style.backgroundColor = FOREST_GREEN.hover) : undefined}
            onMouseLeave={!isPlatform ? (e) => (e.currentTarget.style.backgroundColor = FOREST_GREEN.DEFAULT) : undefined}
          >
            {isSubmitting
              ? 'Creating...'
              : isPlatform
                ? 'Create Platform Product'
                : 'Create Organization Product'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AddProductDialog;
